import {
    DateFieldSingle as AnvilDateField,
    DateFieldSingleProps as AnvilDateFieldProps,
} from '@servicetitan/anvil2';
import { Controller, get, useFormContext } from 'react-hook-form';
import { memo } from 'react';
import { useIsMobile } from '@servicetitan/financing-utils-shared';

type DateFieldProps = Omit<AnvilDateFieldProps, 'error'> & {
    name: string;
};

// Transform date strings to an ISO-like timestamp for the Anvil DateFieldSingle.
// Uses string splitting instead of the Date constructor to avoid timezone shifts
// (e.g. new Date("2025-03-13") parses as UTC midnight, which rolls back a day
// when interpreted in western timezones).
export const formatDateForDisplay = (value: string): string => {
    if (!value) {
        return value;
    }
    if (value.includes('/') && value.length === 10) {
        const [month, day, year] = value.split('/');
        return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}T00:00:00`;
    }
    if (value.includes('-') && value.length === 10) {
        return `${value}T00:00:00`;
    }
    return value;
};

// Ensure form state stores mm/dd/yyyy format (convert from yyyy-mm-dd if needed)
const formatDateForStorage = (value: { date: string }): string => {
    // Convert value to string if it's not already
    const stringValue = typeof value.date === 'string' ? value.date : String(value.date || '');

    if (!stringValue) {
        return stringValue;
    }
    if (stringValue.includes('/')) {
        return stringValue; // Already in mm/dd/yyyy format
    }
    if (stringValue.includes('-') && stringValue.length === 10) {
        // Convert yyyy-mm-dd to mm/dd/yyyy
        const [year, month, day] = stringValue.split('-');
        return `${month.padStart(2, '0')}/${day.padStart(2, '0')}/${year}`;
    }
    return stringValue;
};

const DateFieldComponent = ({ name, ...props }: DateFieldProps) => {
    const {
        control,
        formState: { errors },
    } = useFormContext();
    const isMobile = useIsMobile();

    return (
        <Controller
            control={control}
            name={name}
            rules={{ required: props.required }}
            render={({ field }) => (
                <AnvilDateField
                    mode="mm/dd/yyyy"
                    disableCalendar={isMobile}
                    error={
                        get(errors, field.name) != null ? (
                            // eslint-disable-next-line react/jsx-no-useless-fragment
                            <>{get(errors, field.name)?.message}</>
                        ) : undefined
                    }
                    {...props}
                    type="text"
                    value={formatDateForDisplay(field.value)}
                    onChange={(value: any) => {
                        field.onChange(formatDateForStorage(value));
                        // HACK: DateFieldSingle does not maintain focus on the field when the value is changed
                        // so we need to call onBlur manually to trigger any validations
                        field.onBlur();
                    }}
                    onBlur={field.onBlur}
                    name={field.name}
                    inputMode="numeric"
                />
            )}
        />
    );
};

export const DateField = memo(DateFieldComponent);
